// react
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';

// types
import type { IntakeSummary, IntakeSummaryStatus } from '@karehero/models';

// store
import {
  useConfirmIntakeSummaryMutation,
  useGetIntakeSummaryQuery,
  useRequestIntakeSummaryMutation,
} from 'store/api/inference';

/** The ready event normally ends the wait; polling covers a dropped stream. */
const PENDING_POLL_INTERVAL_MS = 10_000;

const INCOMPLETE_STATUSES: readonly IntakeSummaryStatus[] = [
  'partial',
  'failed',
];

export type IntakeSummaryDisplayStatus = 'reading' | 'read' | 'incomplete';

export interface IntakeSummaryDisplay {
  readonly status: IntakeSummaryDisplayStatus;
  readonly lines: readonly string[];
}

const STILL_READING: IntakeSummaryDisplay = { status: 'reading', lines: [] };
const NOTHING_READ: IntakeSummaryDisplay = { status: 'incomplete', lines: [] };

export type IntakeSummaryRequestStatus =
  'none' | 'requesting' | 'requested' | 'failed';

export interface IntakeSummaryRequest {
  readonly status: IntakeSummaryRequestStatus;
  readonly summaryId: string | null;
}

const NO_REQUEST: IntakeSummaryRequest = { status: 'none', summaryId: null };

export interface UseIntakeSummaryResult extends IntakeSummaryDisplay {
  /** The latest summary while it is unconfirmed: one the member left. */
  unconfirmed?: IntakeSummary;
  /** Carries on with a summary the member left, instead of asking for a new one. */
  onResume: (summary: IntakeSummary) => void;
  /** Resolves once nothing is left to confirm; rejects if the summary could not be confirmed. */
  onConfirm: () => Promise<void>;
}

export interface UseIntakeSummaryOptions {
  careCircleId?: string;
  isEnabled: boolean;
  isActive: boolean;
  /** What has been sent for extraction; a change asks for a new summary. */
  submissionCount: number;
}

// Matched by id so a rejected summary still in the cache is not shown while
// its replacement is on its way.
const requestedSummary = (
  request: IntakeSummaryRequest,
  latest: IntakeSummary | undefined,
): IntakeSummary | undefined =>
  request.status === 'requested' && latest?.id === request.summaryId
    ? latest
    : undefined;

export const displayOf = (
  request: IntakeSummaryRequest,
  latest: IntakeSummary | undefined,
): IntakeSummaryDisplay => {
  if (request.status === 'failed') return NOTHING_READ;

  const summary = requestedSummary(request, latest);
  if (!summary || summary.status === 'pending') return STILL_READING;

  return {
    status: INCOMPLETE_STATUSES.includes(summary.status)
      ? 'incomplete'
      : 'read',
    lines: summary.lines,
  };
};

export const unconfirmedOf = (
  latest: IntakeSummary | undefined,
): IntakeSummary | undefined =>
  latest && !latest.confirmedAt ? latest : undefined;

export const useIntakeSummary = ({
  careCircleId,
  isEnabled,
  isActive,
  submissionCount,
}: UseIntakeSummaryOptions): UseIntakeSummaryResult => {
  const [requestIntakeSummary] = useRequestIntakeSummaryMutation();
  const [confirmIntakeSummary] = useConfirmIntakeSummaryMutation();

  const [request, setRequest] = useState<IntakeSummaryRequest>(NO_REQUEST);
  // How much of what was sent the requested summary covers.
  const [coveredSubmissionCount, setCoveredSubmissionCount] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const isStale =
    request.status !== 'none' && coveredSubmissionCount !== submissionCount;

  const { data: latestData } = useGetIntakeSummaryQuery(
    { careCircleId: careCircleId ?? '' },
    {
      skip: !isEnabled || !careCircleId || !isActive,
      pollingInterval: isPolling ? PENDING_POLL_INTERVAL_MS : 0,
    },
  );
  const latest = latestData ?? undefined;

  // A summary covers what had been sent when it was asked for. More sent since
  // means a new one; nothing new keeps the one they left.
  useEffect(() => {
    if (!isEnabled || !isActive || !careCircleId) return;
    if (request.status !== 'none' && !isStale) return;

    setCoveredSubmissionCount(submissionCount);
    setRequest({ status: 'requesting', summaryId: null });
    requestIntakeSummary({ careCircleId }).then((res) => {
      if ('error' in res) {
        Sentry.captureException(res.error);
        setRequest({ status: 'failed', summaryId: null });
        return;
      }
      setRequest({ status: 'requested', summaryId: res.data.id });
    });
  }, [
    careCircleId,
    isActive,
    isEnabled,
    isStale,
    request.status,
    requestIntakeSummary,
    submissionCount,
  ]);

  // Still reading while a new summary is owed.
  const display = useMemo(
    (): IntakeSummaryDisplay =>
      isStale ? STILL_READING : displayOf(request, latest),
    [isStale, latest, request],
  );

  const shouldPoll =
    isActive && request.status === 'requested' && display.status === 'reading';

  useEffect(() => {
    setIsPolling(shouldPoll);
  }, [shouldPoll]);

  const onResume = useCallback(
    (summary: IntakeSummary) => {
      setCoveredSubmissionCount(submissionCount);
      setRequest({ status: 'requested', summaryId: summary.id });
    },
    [submissionCount],
  );

  const onConfirm = useCallback(async () => {
    // Nothing was read, so there is nothing to confirm.
    if (request.status === 'failed') return;
    if (!careCircleId || display.status === 'reading' || !latest) {
      throw new Error('The intake summary is not ready to confirm');
    }

    const res = await confirmIntakeSummary({
      careCircleId,
      summaryId: latest.id,
    });
    if ('error' in res) {
      throw new Error('The intake summary could not be confirmed', {
        cause: res.error,
      });
    }
  }, [
    careCircleId,
    confirmIntakeSummary,
    latest,
    display.status,
    request.status,
  ]);

  return useMemo(
    () => ({
      ...display,
      unconfirmed: isEnabled ? unconfirmedOf(latest) : undefined,
      onResume,
      onConfirm,
    }),
    [onResume, onConfirm, isEnabled, latest, display],
  );
};
